import type { IAdminCompanyRow } from '@commenda/api/admin';
import {
  Avatar,
  Button,
  ColumnGroup,
  CustomColumnDef,
  DataTable,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  LoadingIndicator,
} from '@commenda/kakashi';
import { useMemo } from 'react';

interface CompanyReviewStepProps {
  selectedCompanies: IAdminCompanyRow[];
  confirmText: string;
  setConfirmText: (text: string) => void;
  isSending: boolean;
  onBack: () => void;
  onSend: () => void;
}

export const CompanyReviewStep = ({
  selectedCompanies,
  confirmText,
  setConfirmText,
  isSending,
  onBack,
  onSend,
}: CompanyReviewStepProps) => {
  const reviewColumns: CustomColumnDef<IAdminCompanyRow, unknown>[] = useMemo(() => [
    {
      id: 'company',
      accessorKey: 'displayName',
      header: 'Company',
      cell: ({ row }) => (
        <div className="max-w-[260px] truncate">
          {row.original.displayName ?? `Company #${row.original.id}`}
        </div>
      ),
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <span>{row.original.email ?? 'N/A'}</span>,
    },
    {
      id: 'sender',
      accessorFn: (row) => row.handler?.name ?? 'N/A',
      header: 'Sender (Account Manager)',
      cell: ({ row }) => (
        <div className="flex items-center gap-sm">
          {row.original.handler?.avatarUrl ? (
            <Avatar
              size="xs"
              image={row.original.handler.avatarUrl}
              altText={row.original.handler.name}
            />
          ) : (
            <Avatar
              size="xs"
              name={row.original.handler?.name ?? '?'}
            />
          )}
          <span>{row.original.handler?.name ?? 'N/A'}</span>
        </div>
      ),
    },
  ], []);

  const reviewColumnGroups: ColumnGroup<IAdminCompanyRow, unknown>[] = useMemo(() => [
    { id: 'main', columns: reviewColumns },
  ], [reviewColumns]);

  return (
    <>
      <DialogHeader className="flex flex-col gap-xs items-start">
        <DialogTitle>Review & Confirm</DialogTitle>
        <DialogDescription>
          Review the recipients below. Each message will be sent from the
          company&apos;s assigned Account Manager into its general thread.
        </DialogDescription>
      </DialogHeader>

      <div className="px-2xl py-xl flex flex-col gap-2xl min-w-0 overflow-y-auto flex-1">
        <div className="overflow-x-auto rounded-md border border-border-primary">
          <DataTable
            columnGroups={reviewColumnGroups}
            data={selectedCompanies}
          />
        </div>

        <div className="shrink-0">
          <label className="txt-small-medium text-text-secondary block mb-sm">
            Type &quot;I confirm&quot; to enable sending
          </label>
          <Input
            placeholder='I confirm'
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="secondary"
          onClick={onBack}
          disabled={isSending}
        >
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onSend}
          disabled={confirmText !== 'I confirm' || isSending}
        >
          {isSending ? (
            <div className="flex items-center gap-sm">
              <LoadingIndicator size={16} />
              Starting...
            </div>
          ) : (
            `Send ${selectedCompanies.length} Message(s)`
          )}
        </Button>
      </DialogFooter>
    </>
  );
};
